import React from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import FirmSwitcher from './FirmSwitcher';
import "./Sidebar.css";

const Sidebar = ({ auth: { user, permissions, isAuthenticated, loading }, sidebar: { menu, isShown, isExpanded } }) => {
    const location = useLocation();
    
    // Hide sidebar if not authenticated
    if (!isAuthenticated && !loading) return null;
    
    if (!isShown) return null;

    const hasPermission = (permission) => {
        if (user?.role_name === 'superadmin') return true;
        
        // Check in all_permissions array (these are the user's actual permissions)
        return permissions?.all_permissions?.includes(permission) || false;
    };

    const getRequiredPermissionForRoute = (route) => {
        // Remove leading slash
        const path = route.replace('/', '');

        // Special cases
        const specialRoutes = {
            'dashboard': 'view dashboard',
            'roles': 'view roles',
            'permissions': 'view permissions',
            'pages': 'view pages',
            'firms': 'view firms',
            'employees': 'view employees',
            'employee-calculations': 'view employee calculations',
            'warehouses': 'view warehouses',
            'spare-parts': 'view spare parts',
            'spare-parts/expenses': 'view sparepart expenses',
            'spare-parts/imports': 'view sparepart imports',
            'transports': 'view transports',
            'machines': 'browse machines',
            'expense-templates': 'view expense templates',
            'units': 'view units',
            'productions': 'view productions',
            'expenses': 'view expenses',
            'supplier-material-calculations': 'view supplier material calculations',
            'product-material-calculations': 'view product material calculations',
            'material-calculations': 'view material calculations',
            'product-calculations': 'view product calculations',
            'supplier-calculations': 'view supplier calculations',
            'client-calculations': 'view client calculations',
            'material-imports': 'view material imports',
        };

        // Return special permission if route exists in mapping
        if (specialRoutes[path]) {
            return specialRoutes[path];
        }

        // Default case: view + route name
        return `view ${path}`;
    };

    const getMenuItems = () => {
        return menu
            .filter(item => {
                // Exclude firms menu item
                if (item.route === '/firms' || item.route?.includes('/firms')) {
                    return false;
                }
                
                // Always show sections if the user has the role
                if (item.is_section) {
                    return item.roles?.includes(user?.role_name) && item.is_visible;
                }

                // For regular menu items, check both role and permission
                const hasRole = item.roles?.includes(user?.role_name);
                const requiredPermission = getRequiredPermissionForRoute(item.route.replace('/', ''));
                
                return hasRole && item.is_visible && hasPermission(requiredPermission);
            })
            .map(item => {
                if (item.is_section) {
                    return {
                        type: 'group',
                        label: item.name,
                        key: item.name
                    };
                }
                
                // Check if this item has submenu
                if (item.submenu && item.submenu.length > 0) {
                    // Filter submenu items based on permissions
                    const filteredSubmenus = item.submenu.filter(subItem => {
                        // Exclude firms submenu items
                        if (subItem.route === '/firms' || subItem.route?.includes('/firms')) {
                            return false;
                        }
                        
                        const hasRole = subItem.roles?.includes(user?.role_name);
                        const requiredPermission = getRequiredPermissionForRoute(subItem.route.replace('/', ''));
                        return hasRole && subItem.is_visible && hasPermission(requiredPermission);
                    });

                    // Only show parent if it has visible children
                    if (filteredSubmenus.length > 0) {
                        return {
                            key: `${item.route}-parent`, // Use unique key for parent with submenu
                            icon: item.icon_tag,
                            label: item.name,
                            children: filteredSubmenus.map(subItem => ({
                                key: subItem.route,
                                label: <Link to={subItem.route}>{subItem.name}</Link>
                            }))
                        };
                    } else {
                        // Show parent as normal link if no children are visible
                        return {
                            key: item.route,
                            icon: item.icon_tag,
                            label: <Link to={item.route}>{item.name}</Link>
                        };
                    }
                } else {
                    // Regular menu item without submenu
                    return {
                        key: item.route,
                        icon: item.icon_tag,
                        label: <Link to={item.route}>{item.name}</Link>
                    };
                }
            })
            .filter((item, index, array) => {
                // Remove empty groups
                if (item.type === 'group') {
                    // Find the next group or end of array
                    const nextGroupIndex = array.findIndex((nextItem, i) => 
                        i > index && nextItem.type === 'group'
                    );
                    const end = nextGroupIndex === -1 ? array.length : nextGroupIndex;
                    // Check if there are any items between this group and the next
                    return array.slice(index + 1, end).some(i => i.type !== 'group');
                }
                return true;
            });
    };

    // Get the current selected key based on location
    const getSelectedKey = () => {
        const pathname = location.pathname;
        // If we're at root, select dashboard
        if (pathname === '/') return '/dashboard';
        
        // Handle spare parts subpages - always select main spare-parts route
        if (pathname.startsWith('/spare-parts')) {
            return '/spare-parts';
        }
        
        // Otherwise use the current path
        return pathname;
    };

    // Get the open keys for submenus
    const getOpenKeys = () => {
        const pathname = location.pathname;
        const openKeys = [];
        
        // Check if current path matches any submenu items
        menu.forEach(item => {
            if (item.submenu && item.submenu.length > 0) {
                const hasActiveSubmenu = item.submenu.some(subItem => 
                    pathname === subItem.route || pathname.startsWith(subItem.route + '/')
                );
                if (hasActiveSubmenu) {
                    openKeys.push(`${item.route}-parent`); // Use the same parent key format
                }
            }
        });
        
        return openKeys;
    };

    return (
        <nav id="sidebar" data-include="sidebar"
             className={`sidebar js-sidebar ${!isExpanded ? 'sidebar-hidden' : ''} `}>
            <div className="sidebar-content">
                <FirmSwitcher />
                
                <div className="sidebar-brand">
                    <span className="align-middle">Boshqaruv menyu</span>
                </div>

                <Menu
                    mode="inline"
                    theme="dark"
                    selectedKeys={[getSelectedKey()]}
                    defaultOpenKeys={getOpenKeys()}
                    items={getMenuItems()}
                />
            </div>
        </nav>
    );
};

const mapStateToProps = state => ({
    auth: state.auth,
    sidebar: state.sidebar
});

export default connect(mapStateToProps)(Sidebar);
